// ./src/utils/api/ServicePointService.ts
//
// ROADMAP #169 — service points: named points of a venue (reused by every event there) or of one event — entrances,
// desks, merch tables, bars. A venue's are saved WITH the venue (`VenueInput.servicePoints`); an event's own here
// (`setForEvent`, the whole list); a scanning page reads `forEvent` (owner or crew — door crew cannot read the venue).
// Everything returns an OpResult.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export const SERVICE_POINT_FIELDS = `
  id
  name
  kind
  activity
  notes
  phone
  guestNote
  address
  latitude
  longitude
  photoUploadedFileId
  photoUploadedFile { fileUrl thumbnailUrl }
  venueId
  venueName
  venueAddressId
  eventId
  orderNo
`;

/** What a recorded scan says about where it happened. */
export const SERVICE_POINT_REF_FIELDS = `id name kind removedAt`;

const FOR_EVENT_QUERY = gql`
  query ServicePointsForEvent($params: ServicePointsForEventInput) {
    servicePointsForEvent(params: $params) {
      code
      errors { name errors }
      data { ${SERVICE_POINT_FIELDS} }
    }
  }
`;

const SET_FOR_EVENT_MUTATION = gql`
  mutation ServicePointSetForEvent($params: ServicePointSetForEventInput) {
    servicePointSetForEvent(params: $params) {
      code
      errors { name errors }
      data { ${SERVICE_POINT_FIELDS} }
    }
  }
`;

export interface ServicePoint {
  id: string;
  name: string;
  kind: string;
  /** #176 — check_in | hand_out | both; null = the kind's default (`pointActivity` reads it). */
  activity?: string | null;
  notes?: string | null;
  /** #68 — what guests are told: a number to call, how to find it, where it is, a picture. */
  phone?: string | null;
  guestNote?: string | null;
  /** What the pin is — an address or the host's own words. */
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  photoUploadedFileId?: string | null;
  photoUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** The venue it belongs to; null for an event's own point. */
  venueId?: string | null;
  venueName?: string | null;
  venueAddressId?: string | null;
  /** The event it belongs to; null for a venue's point. */
  eventId?: string | null;
  orderNo?: number | null;
}

/** A point as a recorded scan names it — `removedAt` set when it was removed since. */
export interface ServicePointRef {
  id: string;
  name?: string | null;
  kind?: string | null;
  removedAt?: string | null;
}

/** One row of a saved list: with an id → updated; without → created. */
export interface ServicePointInput {
  id?: string | null;
  name: string;
  kind: string;
  /** #176 — null = follow the kind. ⚠ Always sent: the server keeps the stored one when it is absent. */
  activity: string | null;
  notes?: string | null;
  /** #68 — what guests are told. ⚠ Always sent by the editor: the server keeps the stored ones when they are absent. */
  phone?: string | null;
  guestNote?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  photoUploadedFileId?: string | null;
  /** A venue point only: one of the venue's saved addresses. */
  venueAddressId?: string | null;
}

export class ServicePointService {
  /** Every live point at an event — its venue's, each session's venue's, then its own. */
  static async forEvent(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(FOR_EVENT_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** Replace the event's OWN points (refusals keyed `items[<index>].<field>`). Answers with all the event's points. */
  static async setForEvent(eventId: string, items: ServicePointInput[]): Promise<OpResult> {
    return ApiService.executeMutation(SET_FOR_EVENT_MUTATION, { params: { eventId, items } });
  }
}
